import { CreditIcon } from "@/components/features/Credits/CreditIcon";
import { DailyLimitPhrase } from "@/components/features/Credits/DailyLimitPhrase";
import { useNow } from "@/components/features/Credits/useNow";
import { Button } from "@/design-system/Button/Button";
import { trackUpgrade } from "@/events/subscriptionMixpanel";
import type { IRootState } from "@/redux/store";
import { userIsLoggedInSelector } from "@/redux/user/userSlice";
import { formatResetCountdown, resetTooltip } from "@repo/shared/credits";
import { Trans, useTranslation } from "@repo/shared/i18n";
import type { CreditFeatureId } from "@repo/shared/redux";
import {
  creditsAreSilentSelector,
  creditsTotalSelector,
  dailyLimitSelector,
  dailyResetsAtSelector,
  hasSignupBonusBucketSelector,
  signupBonusGrantSelector,
  signupBonusRemainingSelector,
} from "@repo/shared/redux";
import { ASSUMED_COST } from "@repo/shared/subscription";
import { useSelector } from "react-redux";

/*
  The rows of the cost table, in render order. Ids match the keys of the
  `features` map in `GET /credits`, so a cost is a direct index; the names are
  `CREDIT_FEATURES[id].label` moved into the locale files. The free half below
  has no backend counterpart — it is the answer to "what does this cost me",
  and the last row is there so the list reads as complete rather than partial.
*/
const PRICED_FEATURES: CreditFeatureId[] = [
  "solve-sbc-club",
  "build-squad-club",
  "squad-recommendations-club",
  "evo-chains-club",
  "gallery-solutions-built",
  "gallery-level-plan-built",
];

const FREE_ACTIONS = [
  "importClub",
  "profit",
  "clubStats",
  "squadBuilderAi",
  "everythingElse",
] as const;

/*
  Placeholder for DEV-3266 — the shaped design isn't finalised. Deliberately
  plain: it exists so the subscription page has something truthful to say about
  credits, and so the nav chip has somewhere to link. Expect it to be replaced
  wholesale rather than extended.
*/

/**
 * "resets in 6h" — the daily refill, attached to the sentence that promises it
 * rather than to the balance, which is the whole pot and doesn't all come back.
 */
const ResetCountdown = () => {
  const resetsAt = useSelector(dailyResetsAtSelector);
  const now = useNow();
  const { t } = useTranslation("subscription");

  if (!resetsAt) return null;

  return (
    <>
      {t("creditsExplainer.resetsIn")}
      <span
        title={resetTooltip(resetsAt)}
        className="cursor-help border-b border-dotted border-amber-400/60 text-amber-400"
      >
        {formatResetCountdown(resetsAt, now)}
      </span>
    </>
  );
};

/**
 * The balance, as a headline rather than a sentence: one number and what it is.
 *
 * The number is the whole balance, not the daily bucket, so it carries no
 * denominator — "3 of 5" belongs to the nav chip, which reports today's
 * allowance.
 */
const BalanceSummary = () => {
  const total = useSelector(creditsTotalSelector);
  const { t } = useTranslation("subscription");

  if (typeof total !== "number") return null;

  return (
    <div className="mb-5 flex items-center gap-3 rounded-xl border border-neutral-800 bg-panel-background px-5 py-4">
      <CreditIcon size="large" />
      <div>
        <p className="text-2xl font-bold leading-tight tabular-nums text-foreground">
          {total}
        </p>
        <p className="text-sm text-foreground-muted">
          {t("creditsExplainer.available", {
            credits: t("credits.noun", { count: total }),
          })}
        </p>
      </div>
    </div>
  );
};

type Props = {
  /** Scrolls back to the pricing cards, which this section can't reach itself. */
  onSeePlans: () => void;
};

export const CreditsExplainer = ({ onSeePlans }: Props) => {
  const { t } = useTranslation(["subscription", "common"]);
  const isLoggedIn = useSelector(userIsLoggedInSelector);
  const isSilent = useSelector(creditsAreSilentSelector);
  const total = useSelector(creditsTotalSelector);
  const dailyLimit = useSelector(dailyLimitSelector);
  const bonusRemaining = useSelector(signupBonusRemainingSelector);
  const bonusGrant = useSelector(signupBonusGrantSelector);
  const hasBonusBucket = useSelector(hasSignupBonusBucketSelector);
  const featureCosts = useSelector(
    (state: IRootState) => state.permissionsReducer.featureCosts,
  );

  // A logged-out visitor never fetches /credits, and a user still on their
  // sign-up bonus isn't shown a balance anywhere else — so the balance line is
  // the one part of this section that isn't always available.
  const showBalance = isLoggedIn && !isSilent && total !== "unlimited";

  /*
    The bonus is the one credit surface this page shows while the silence rule
    is still on: a deliberate visit to the pricing page is where the gift gets
    explained, and "you already have 17 free credits" is the friendliest
    possible first mention of the system.

    Unlimited tiers are excluded — they were never granted a bonus, and an
    absent bucket can't tell "spent it" from "never had one".
  */
  const showBonus = isLoggedIn && hasBonusBucket && total !== "unlimited";

  return (
    <section className="mx-auto w-full max-w-[720px] px-4 pb-12 text-white mt-12">
      <h2 className="text-center text-2xl font-semibold">
        {t("creditsExplainer.heading")}
      </h2>

      {/*
        What a credit *is* comes before what the user has of them. The reader
        arrives not knowing why a credit exists at all, and a balance means
        nothing until they do.
      */}
      <div className="mt-6 flex flex-col gap-2.5 rounded-2xl border border-neutral-700/70 bg-panel-base p-6 text-sm text-neutral-200">
        {/*
          "Heavy computation" is the reason, and it does two jobs: it explains
          why a meter exists at all, and it explains why plenty of club-aware AI
          stays free — reading your club is cheap, searching it is not.
        */}
        <p>{t("creditsExplainer.what")}</p>
        <p>
          {/* The reassurance is the point of the section, so it gets the accent. */}
          <span className="font-semibold text-primary-blue">
            {t("creditsExplainer.freeAccent")}
          </span>{" "}
          {t("creditsExplainer.freeTail")}
        </p>
      </div>

      {/*
        Then what they have. The examples that used to sit above live in the
        table below instead, where a reader who wants the detail will find them
        without being made to read a list first.
      */}
      <div className="mt-4 rounded-2xl border border-neutral-700/70 bg-panel-base p-6">
        {showBalance && <BalanceSummary />}
        <ul className="flex flex-col gap-2.5 text-sm text-neutral-200">
          <li>
            <Trans
              ns="subscription"
              i18nKey="creditsExplainer.dailyGrant"
              components={{
                phrase: <DailyLimitPhrase limit={dailyLimit} />,
                reset: showBalance ? <ResetCountdown /> : <></>,
              }}
            />
          </li>
          {showBonus && (
            <li>
              {bonusRemaining > 0
                ? t("creditsExplainer.welcomeRemaining", {
                    remaining: bonusRemaining,
                    count: bonusGrant,
                  })
                : t("creditsExplainer.welcomeSpent", { count: bonusGrant })}
            </li>
          )}
        </ul>
      </div>

      <h3 className="mt-8 text-center text-lg font-semibold">
        {t("creditsExplainer.tableHeading")}
      </h3>

      {/*
        Both halves in one table, deliberately. A table of costs alone reads as
        a list of what is taken away; the free rows are the larger half of the
        truth, and seeing them in the same column makes the meter look as small
        as it is.
      */}
      <div className="mt-4 overflow-hidden rounded-2xl border border-neutral-700/70">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="bg-panel-background text-xs uppercase tracking-wider text-foreground-muted">
              <th className="px-5 py-3 font-medium">
                {t("creditsExplainer.action")}
              </th>
              <th className="px-5 py-3 text-right font-medium">
                {t("creditsExplainer.cost")}
              </th>
            </tr>
          </thead>
          <tbody>
            {PRICED_FEATURES.map((id) => {
              const cost = featureCosts[id] ?? ASSUMED_COST;
              return (
                <tr
                  key={id}
                  className="border-t border-neutral-800 odd:bg-white/[0.02]"
                >
                  <td className="px-5 py-3.5">{t(`credits.features.${id}`)}</td>
                  <td className="px-5 py-3.5 text-right">
                    <span className="inline-flex items-center gap-1.5 font-semibold tabular-nums">
                      <CreditIcon />
                      {cost}
                    </span>
                  </td>
                </tr>
              );
            })}
            {FREE_ACTIONS.map((action) => (
              <tr
                key={action}
                className="border-t border-neutral-800 odd:bg-white/[0.02]"
              >
                <td className="px-5 py-3.5 text-foreground-muted">
                  {t(`creditsExplainer.freeActions.${action}`)}
                </td>
                <td className="px-5 py-3.5 text-right font-semibold text-foreground-muted">
                  {t("common:free")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-8 flex flex-col items-center gap-3 rounded-2xl border border-primary-blue/25 bg-linear-to-b from-primary-blue/15 to-transparent px-6 py-7 text-center">
        {/*
          Framed on what the reader gains rather than on the limit they just
          read about — the limit is ours, the squad is theirs.
        */}
        <h3 className="text-lg font-semibold text-white">
          {t("creditsExplainer.upsell.heading")}
        </h3>
        <p className="max-w-[420px] text-sm text-foreground-muted">
          {t("creditsExplainer.upsell.body")}
        </p>
        <Button
          variant="primary"
          size="medium"
          className="mt-1"
          onClick={() => {
            trackUpgrade("Bottom");
            onSeePlans();
          }}
        >
          {t("creditsExplainer.upsell.cta")}
        </Button>
      </div>
    </section>
  );
};
